import React, { useEffect, useState } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";
import { getSelectedPlanName } from "../shared/_helpers/selectedPlan";

const ADMIN_ROLES = ["super-admin", "admin"];

function isAdminRole(role) {
  return ADMIN_ROLES.includes(role);
}

function getUserInitials(item) {
  const firstInitial = item?.firstName ? item.firstName.trim().charAt(0) : "";
  const lastInitial = item?.lastName ? item.lastName.trim().charAt(0) : "";
  return `${firstInitial}${lastInitial}`;
}

// The roster's plan column (#90). It matched only the legacy lookup keys
// ('Platinum price' / 'Essential price'), but current keys are
// `${nickname}-${hex}`, so every plan created since rendered '-'. The shared
// resolver reads the price nickname from either `selected_plan` shape.
// Exported for tests.
export function getSubscriptionTypeLabel(item) {
  return getSelectedPlanName(item?.selected_plan);
}

function formatMemberSince(createdAt) {
  return createdAt ? moment(createdAt).format("Do MMMM YYYY") : "N/A";
}

function TableProfileAvatar({ item }) {
  if (item?.profile_image) {
    return <img src={`${process.env.REACT_APP_BACKEND}/uploads/${item.profile_image}`} alt="Profile" />;
  }
  return <h3>{getUserInitials(item)}</h3>;
}

function EditIcon() {
  return (
    <svg
      width="17"
      height="19"
      viewBox="0 0 17 19"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10.4999 3.99994L12.9999 6.99994M8.83325 17.9999H15.4999M2.16659 13.9999L1.33325 17.9999L4.66659 16.9999L14.3216 5.41394C14.634 5.03889 14.8096 4.53027 14.8096 3.99994C14.8096 3.46961 14.634 2.961 14.3216 2.58594L14.1783 2.41394C13.8657 2.039 13.4419 1.82837 12.9999 1.82837C12.558 1.82837 12.1341 2.039 11.8216 2.41394L2.16659 13.9999Z"
        stroke="#1A1D43"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function BlockIcon({ status }) {
  return (
    <svg
      width="24"
      height="24"
      className={status ? "" : "blocked-svg"}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_2411_158)">
        <path
          d="M15.5962 13.4475C15.3937 13.575 15.1987 13.7062 15.0112 13.8412C14.8237 13.9762 14.64 14.1337 14.46 14.3137C13.7925 13.7137 13.0425 13.26 12.21 12.9525C11.3775 12.645 10.5075 12.4875 9.59996 12.48C8.59496 12.48 7.65371 12.6637 6.77621 13.0312C5.89871 13.3987 5.13746 13.9087 4.49246 14.5612C3.84746 15.2137 3.33746 15.9787 2.96246 16.8562C2.58746 17.7337 2.39996 18.675 2.39996 19.68H0.959961C0.959961 18.78 1.09121 17.91 1.35371 17.07C1.61621 16.23 1.99121 15.4575 2.47871 14.7525C2.96621 14.0475 3.55121 13.4212 4.23371 12.8737C4.91621 12.3262 5.68871 11.8987 6.55121 11.5912C6.13871 11.3287 5.76746 11.0212 5.43746 10.6687C5.10746 10.3162 4.82246 9.92621 4.58246 9.49871C4.34246 9.07121 4.15871 8.62871 4.03121 8.17121C3.90371 7.71371 3.83996 7.22996 3.83996 6.71996C3.83996 5.92496 3.98996 5.17871 4.28996 4.48121C4.58996 3.78371 5.00246 3.17621 5.52746 2.65871C6.05246 2.14121 6.66371 1.72871 7.36121 1.42121C8.05871 1.11371 8.80496 0.959961 9.59996 0.959961C10.395 0.959961 11.1412 1.10996 11.8387 1.40996C12.5362 1.70996 13.1437 2.12246 13.6612 2.64746C14.1787 3.17246 14.5912 3.78371 14.8987 4.48121C15.2062 5.17871 15.36 5.92496 15.36 6.71996C15.36 7.21496 15.2962 7.69496 15.1687 8.15996C15.0412 8.62496 14.8612 9.07121 14.6287 9.49871C14.3962 9.92621 14.1112 10.3125 13.7737 10.6575C13.4362 11.0025 13.0612 11.3137 12.6487 11.5912C13.2037 11.7862 13.725 12.0412 14.2125 12.3562C14.7 12.6712 15.1612 13.035 15.5962 13.4475ZM5.27996 6.71996C5.27996 7.31246 5.39246 7.87121 5.61746 8.39621C5.84246 8.92121 6.15371 9.37871 6.55121 9.76871C6.94871 10.1587 7.40621 10.4662 7.92371 10.6912C8.44121 10.9162 8.99996 11.0325 9.59996 11.04C10.1925 11.04 10.7512 10.9275 11.2762 10.7025C11.8012 10.4775 12.2587 10.1662 12.6487 9.76871C13.0387 9.37121 13.3462 8.91371 13.5712 8.39621C13.7962 7.87871 13.9125 7.31996 13.92 6.71996C13.92 6.12746 13.8075 5.56871 13.5825 5.04371C13.3575 4.51871 13.0462 4.06121 12.6487 3.67121C12.2512 3.28121 11.7937 2.97371 11.2762 2.74871C10.7587 2.52371 10.2 2.40746 9.59996 2.39996C9.00746 2.39996 8.44871 2.51246 7.92371 2.73746C7.39871 2.96246 6.94121 3.27371 6.55121 3.67121C6.16121 4.06871 5.85371 4.52621 5.62871 5.04371C5.40371 5.56121 5.28746 6.11996 5.27996 6.71996Z"
          fill="#1A1D43"
        />
        <path
          d="M18 10.5601C18.8636 10.5601 19.6714 10.7226 20.4236 11.0476C21.1757 11.3726 21.835 11.8183 22.4014 12.3847C22.9679 12.9511 23.4136 13.6104 23.7386 14.3626C24.0636 15.1147 24.2307 15.9272 24.24 16.8001C24.24 17.6636 24.0775 18.4715 23.7525 19.2236C23.4275 19.9758 22.9818 20.6351 22.4154 21.2015C21.8489 21.7679 21.1897 22.2136 20.4375 22.5386C19.6854 22.8636 18.8729 23.0308 18 23.0401C17.1364 23.0401 16.3286 22.8776 15.5764 22.5526C14.8243 22.2276 14.165 21.7818 13.5986 21.2154C13.0322 20.649 12.5864 19.9897 12.2614 19.2376C11.9364 18.4854 11.7693 17.6729 11.76 16.8001C11.76 15.9365 11.9225 15.1286 12.2475 14.3765C12.5725 13.6243 13.0182 12.9651 13.5847 12.3986C14.1511 11.8322 14.8104 11.3865 15.5625 11.0615C16.3147 10.7365 17.1272 10.5693 18 10.5601ZM18 21.2572C18.4364 21.2572 18.8636 21.1968 19.2814 21.0761C19.6993 20.9554 20.0893 20.7697 20.4514 20.519L14.2811 14.3486C14.0397 14.7108 13.8586 15.1008 13.7379 15.5186C13.6172 15.9365 13.5522 16.3636 13.5429 16.8001C13.5429 17.4129 13.6589 17.9886 13.8911 18.5272C14.1232 19.0658 14.4436 19.5347 14.8522 19.934C15.2607 20.3333 15.7343 20.6536 16.2729 20.8951C16.8114 21.1365 17.3872 21.2572 18 21.2572ZM21.7189 19.2515C21.9604 18.8893 22.1414 18.4993 22.2622 18.0815C22.3829 17.6636 22.4479 17.2365 22.4572 16.8001C22.4572 16.1872 22.3411 15.6115 22.1089 15.0729C21.8768 14.5343 21.5564 14.0654 21.1479 13.6661C20.7393 13.2668 20.2657 12.9465 19.7272 12.7051C19.1886 12.4636 18.6129 12.3429 18 12.3429C17.5636 12.3429 17.1364 12.4033 16.7186 12.524C16.3007 12.6447 15.9107 12.8304 15.5486 13.0811L21.7189 19.2515Z"
          fill="#1A1D43"
        />
      </g>
      <defs>
        <clipPath id="clip0_2411_158">
          <rect width="24.96" height="24" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

// A tool image comes from blob storage and can legitimately be unavailable — a
// blob that never made it through the migration, a row whose file was deleted
// underneath it, or any transient 5xx from the CDN.
//
// The old handler answered that by reassigning `src` to "/placeholder.png",
// which is not in `public/`. The app origin answers an unknown path with
// `index.html`, an <img> handed HTML fires `error`, and the handler reassigned
// the same src — so a single missing image put the row in a loop it could never
// leave. It emitted tens of thousands of requests, and once that HTML response
// was cached it kept spinning with no network traffic at all, which is the state
// where the table renders but its buttons can no longer be clicked.
//
// Failing over to an element rather than another URL makes re-entry impossible:
// once `failed` is set there is no <img> left to fire `error`.
function InvestmentToolImage({ src, alt }) {
  const [failed, setFailed] = useState(false);

  // A re-upload changes `src`, which deserves a fresh attempt.
  useEffect(() => setFailed(false), [src]);

  if (failed) {
    return (
      <span className="text-muted" style={{ fontSize: "0.75rem" }}>
        Image unavailable
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      style={{ maxWidth: "100px", maxHeight: "100px", objectFit: "contain" }}
      onError={() => setFailed(true)}
    />
  );
}

// The `?t=` exists so that re-uploading under the same file name is not served
// from cache. It used to be `Date.now()`, evaluated during render, which busted
// the cache on every re-render: the image could never be cached and each render
// refetched all of them. `updatedAt` changes exactly when the tool document
// does, which is the only time the image can have changed.
function buildToolImageSrc(item) {
  const base = `${process.env.REACT_APP_BACKEND}/${item.filePath}`;
  const version = item.updatedAt || item.uploadedAt;
  return version ? `${base}?t=${encodeURIComponent(version)}` : base;
}

function InvestmentToolRow({
  item,
  role,
  onToggleToolClick,
  onTogglePriorityClick,
  onEditToolClick,
}) {
  const canManage = isAdminRole(role);
  const toolName = item.fileName.replace(/\.[^/.]+$/, "");

  return (
    <>
      <td>{toolName}</td>
      <td>
        <InvestmentToolImage src={buildToolImageSrc(item)} alt={toolName} />
      </td>
      <td>{item.isEnabled ? "Enabled" : "Disabled"}</td>
      {canManage && (
        <td>
          <div
            id="toggle-tool"
            className="cursor-pointer"
            onClick={() => onToggleToolClick(item)}
          >
            <svg
              width="40"
              height="20"
              viewBox="0 0 40 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={item.isEnabled ? "" : "blocked-svg"}
            >
              <rect
                x="1"
                y="1"
                width="38"
                height="18"
                rx="9"
                fill={item.isEnabled ? "#63363A" : "#D3D3D3"}
                stroke={item.isEnabled ? "#63363A" : "#D3D3D3"}
                strokeWidth="2"
              />
              <circle cx={item.isEnabled ? "31" : "9"} cy="10" r="8" fill="white" />
            </svg>
          </div>
        </td>
      )}
      {canManage && (
        <>
          <td>
            <div
              id="priority-tool"
              className="cursor-pointer"
              onClick={() => onTogglePriorityClick(item)}
            >
              <input
                type="checkbox"
                checked={item.isTopPriority || false}
                onChange={() => {}}
                style={{ cursor: "pointer" }}
              />
            </div>
          </td>
          <td>
            <div
              id="edit-advisor"
              className="cursor-pointer"
              onClick={() => onEditToolClick(item)}
            >
              <EditIcon />
            </div>
          </td>
        </>
      )}
    </>
  );
}

function DefaultTableRow({
  item,
  headers,
  role,
  onEditAdvisorClick,
  onBlockUserClick,
  handleUserDetailClick,
}) {
  const canManage = isAdminRole(role);

  return (
    <>
      <td onClick={() => handleUserDetailClick(item._id)} className="cursor-pointer">
        <div className="user_name">
          <div className="profile-pic">
            <TableProfileAvatar item={item} />
          </div>
          {item?.firstName} {item?.lastName}
        </div>
      </td>
      {headers?.includes("Financial Advisor") && (
        <td>
          {item?.advisor?.firstName ? item?.advisor?.firstName : "-"} {item?.advisor?.lastName}
        </td>
      )}
      {headers?.includes("Subscription Type") && (
        <td>{getSubscriptionTypeLabel(item)}</td>
      )}
      <td>{item?.email}</td>
      {headers?.includes("Phone Number") && <td>{item?.mobile}</td>}
      {headers?.includes("No.Clients") && <td>{item?.uniqueClientCount}</td>}
      {headers?.includes("Edit") && canManage && (
        <td>
          <div
            id="edit-advisor"
            className="cursor-pointer"
            onClick={() => onEditAdvisorClick(item)}
          >
            <EditIcon />
          </div>
        </td>
      )}
      {headers?.includes("Block") && canManage && (
        <td>
          <div
            id="block-advisor"
            className="cursor-pointer"
            onClick={() => onBlockUserClick(item)}
          >
            <BlockIcon status={item.status} />
          </div>
        </td>
      )}
      {headers?.includes("Member Since") && (
        <td>{formatMemberSince(item.createdAt)}</td>
      )}
    </>
  );
}

const TableComponent = ({ headers, data, test, onEditAdvisorClick, onBlockUserClick, onBlockAdvisorClick, onToggleToolClick, onTogglePriorityClick, onEditToolClick , onDownloadToolClick, onDeleteToolClick }) => {
  const sortedData = data ? [...data].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) : [];
  const navigate = useNavigate();
  // SEC-066: the edit/block columns are gated by the server-verified role —
  // this table only ever renders inside a ProtectedRoutes subtree, so
  // verification has already settled by the time it mounts.
  const { role } = useAuth();
  const handleUserDetailClick = (clientId) => {
    test === "temp" && navigate(`/dashboard/${clientId}?client=true`);
  };

  return (
    <div className="table-responsive">
      <table className="AdvisorUserList_table">
        <thead>
          <tr>
            {headers?.map((header) => (
              <th key={header}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData?.length > 0 ? (
            sortedData?.map((item) => (
              <tr key={item._id}>
                {test === "investment-tool" ? (
                  <InvestmentToolRow
                    item={item}
                    role={role}
                    onToggleToolClick={onToggleToolClick}
                    onTogglePriorityClick={onTogglePriorityClick}
                    onEditToolClick={onEditToolClick}
                  />
                ) : (
                  <DefaultTableRow
                    item={item}
                    headers={headers}
                    role={role}
                    onEditAdvisorClick={onEditAdvisorClick}
                    onBlockUserClick={onBlockUserClick}
                    handleUserDetailClick={handleUserDetailClick}
                  />
                )}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={headers?.length || 1} className="text-center mt-4">
                No data found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default TableComponent;